import Checkbox from '@mui/material/Checkbox';
import classNames from 'classnames';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import type { ChangeEventHandler, FC, VFC } from 'react';

import { CodeSentryFetcherName } from '../../../../../shared/fetchers';
import { pluralizeIssues } from '../../../../../shared/helpers';
import { renderRevision } from '../../../../../shared/helpers/renderLocator';
import { IssueScopeKind } from '../../../../../shared/issuesV2';
import Locator from '../../../../../shared/Locator';
import { useGetDependencyConfidence } from '../../../../hooks/api';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import {
  collapseIssueRevision,
  expandIssueRevision,
  fetchIssuesForRevision,
  toggleSelectRevision,
} from '../../../../redux/issuesV2/actions';
import { isFullHash, onEnter } from '../../../../utils';
import {
  Badge,
  BadgeColors,
  BadgeShapes,
  BUTTON_SIZE,
  BUTTON_TYPE,
  Icon,
  icons,
  InlineLoaderButton,
  IssueCountBadge,
} from '../../../generic';
import FetcherDetail from '../../../generic/FetcherDetail';
import Flex from '../../../generic/Flex';
import { ICON_SIZE } from '../../../generic/Icon/icon-constants';
import List from '../../../generic/List';
import Tooltip from '../../../generic/Tooltip';
import UpgradeBadge from '../../../packageInventory/ImpactAssessment/UpgradeBadge';
import BinaryComponentBadge from '../../../shared/revisions/BinaryComponentBadge';
import type { IssueRevision } from '../../../../../shared/issuesV2';
import type { IssueList, IssueRevisionWithIssuesAndFixDetails } from '../../../../redux/issuesV2/types';

import IssueItem from '.';
import IssueItemLoading from './IssueItemLoading';

import css from './RevisionItem.module.less';

// Number of revision issues to load at a time
const REVISION_ISSUE_PAGE_SIZE = 10;

type DepthProps = {
  revision: IssueRevision,
  // Scope is passed down because the tooltip is not a child of the redux
  // provider and cannot access the state.
  scope: IssueScopeKind,
};

const DepthTooltip: VFC<DepthProps> = ({ revision, scope }) => {
  const { direct, transitive } = revision.depth;

  if (scope === IssueScopeKind.Project) {
    if (direct) {
      return <>Direct dependency of this project</>;
    }
    return <>Transitive dependency of this project</>;
  }

  if (direct && transitive) {
    return (
      <ul className={css.depthTooltip}>
        <li>Direct dependency in {direct} projects</li>
        <li>Transitive dependency in {direct} projects</li>
      </ul>
    );
  }
  if (direct) {
    return <>Direct dependency in {direct} projects</>;
  }
  if (transitive) {
    return <>Transitive dependency in {transitive} projects</>;
  }
  return null;
};

const DepthBadge: VFC<DepthProps> = ({ revision, scope }) => {
  const { direct, transitive } = revision.depth;

  const directStr = scope === IssueScopeKind.Project ? 'Direct' : `Direct (${direct})`;
  const transitiveStr = scope === IssueScopeKind.Project ? 'Transitive' : `Transitive (${transitive})`;

  if (direct && transitive) {
    return <Badge color={BadgeColors.White}>{`${directStr} + ${transitiveStr}`}</Badge>;
  }
  if (direct) {
    return <Badge color={BadgeColors.White}>{directStr}</Badge>;
  }
  if (transitive) {
    return <Badge color={BadgeColors.White}>{transitiveStr}</Badge>;
  }
  return null;
};

/** The dependency badge. */
const DepthDetails: VFC<DepthProps> = ({ revision, scope }) => (
  // TODO(PIG): Is there a generic version of this component anywhere else?

  <Tooltip
    id={`Revision-Depth-For-${revision.revisionId}`}
    tooltip={<DepthTooltip revision={revision} scope={scope} />}
    >
    {/* Note: The Tooltip needs to inject trigger props into a DOM element somewhere, which is simplest with a DOM node itself. */}
    <span>
      <DepthBadge revision={revision} scope={scope} />
    </span>
  </Tooltip>
);

type LoadIssuesButtonProps = {
  onClick: () => void;
  isLoading: boolean;
};

/**
 * Button used for various "load more issues" situations. Stops event propagation
 * to prevent the accordion from toggling its open / closed state.
 */
const LoadIssuesButton: FC<LoadIssuesButtonProps> = ({ children, onClick, isLoading }) => (
  // TODO(CORE-2230): Sync styling with design / mock
  <div className={css.loadIssuesButton}>
    <InlineLoaderButton
      type={BUTTON_TYPE.SECONDARY}
      size={BUTTON_SIZE.MEDIUM}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      isLoading={isLoading}
    >
      {isLoading ? 'Loading' : children}
    </InlineLoaderButton>
  </div>
);

type RevisionItemProp = IssueRevisionWithIssuesAndFixDetails<IssueList>;

const RevisionItem: VFC<{ revision: RevisionItemProp }> = ({ revision }) => {
  const dispatch = useDispatch();
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const isExpanded = useSelector(IssuesV2Store.selectors.isRevisionExpanded(revision.revisionId));
  const { fetcher } = Locator.parse(revision.revisionId);
  const category = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const isSelected = useSelector(IssuesV2Store.selectors.getIsRevisionSelected(revision));
  const isAnyIssueSelected = useSelector(IssuesV2Store.selectors.getIsAnyIssueForRevisionSelected(revision));

  const remainingIssues = revision.issueCount - revision.issues.length;
  const loadingRowCount = Math.min(REVISION_ISSUE_PAGE_SIZE, revision.issueCount);

  const version = renderRevision(revision.revisionId);
  const versionClassName = classNames(css.revision, { [css.truncated]: isFullHash(version ?? '') });

  const projectRevision = scope.type === 'project' ? `${scope.id}$${scope.revision}` : '';
  const { confidence, isLoading } = useGetDependencyConfidence(projectRevision, revision.revisionId);
  const isBinaryComponent = fetcher === CodeSentryFetcherName;

  const shouldDisplayConfidenceBadge = isBinaryComponent;
  const shouldDisplayFetcher = !shouldDisplayConfidenceBadge && fetcher !== null;

  const toggleRevisionAccordion = () => {
    if (isExpanded) {
      dispatch(collapseIssueRevision(revision.revisionId));
    } else {
      dispatch(expandIssueRevision(revision.revisionId));
    }
  };

  const onSelectionChange: ChangeEventHandler<HTMLInputElement> = () => {
    dispatch(toggleSelectRevision(revision.revisionId));
  };

  return (
    <List.RowContainer key={revision.revisionId}>
      <div
        className={css.row}
        role="button"
        onClick={toggleRevisionAccordion}
        tabIndex={0}
        onKeyDown={onEnter(toggleRevisionAccordion)}
      >
        <List.Row
          hasCheckbox
          left={
            <div className={css.actions}>
              <div className={css.checkbox}>
                <Checkbox
                  checked={isExpanded}
                  color="default"
                  icon={<Icon icon={icons.angleRight} size={ICON_SIZE.SM} />}
                  checkedIcon={<Icon icon={icons.angleDown} size={ICON_SIZE.SM} />}
                />
              </div>
              <div className={css.checkbox}>
                <Checkbox
                  indeterminate={!isSelected && isAnyIssueSelected}
                  checked={isSelected}
                  onClick={(e) => e.stopPropagation()} // Don't toggle the accordion when clicking the checkbox
                  onChange={onSelectionChange}
                />
              </div>
            </div>
          }
          right={
            revision.type && (
              <Flex direction="row" gap="5px">
                <UpgradeBadge
                  upgradeVersion={revision.completeFix?.nextSafeVersion || ''}
                  fixCount={revision.issueCount}
                  />
                <IssueCountBadge
                  id={revision.revisionId}
                  issueTypes={revision.type}
                  pluralLabel={pluralizeIssues}
                  color={BadgeColors.LighterGray}
                  shape={BadgeShapes.Circle}
                  />
              </Flex>
            )
          }
        >
          <List.RowTitle>
            {revision.dependencyName}
            {version && (
              <Tooltip id={`revision-item-version-${revision.revisionId}`} tooltip={version}>
                <span className={css.revision}>
                  &nbsp;(<span className={versionClassName}>{version}</span>)
                </span>
              </Tooltip>
            )}
          </List.RowTitle>
          <List.RowDetails>
            {shouldDisplayFetcher && (
              <List.RowDetailsItem>
                <FetcherDetail fetcher={fetcher} />
              </List.RowDetailsItem>
            )}
            {shouldDisplayConfidenceBadge && (
              <List.RowDetailsItem>
                <BinaryComponentBadge
                  confidence={confidence}
                  isLoading={isLoading}
                />
              </List.RowDetailsItem>
            )}
            <List.RowDetailsItem trailingSlot={<DepthDetails revision={revision} scope={scope.type} />}>
              Depth
            </List.RowDetailsItem>
            {scope.type !== IssueScopeKind.Project && (
              <List.RowDetailsIconItem icon={icons.folders} count={revision.projectCount}>
                Projects
              </List.RowDetailsIconItem>
            )}
          </List.RowDetails>
        </List.Row>
      </div>
      {isExpanded && (
        <div className={css.content}>
          <div className={css.issuesContainer}>
            {revision.issues.map((issue) => (
              <IssueItem key={issue.id} category={category} issue={issue} />
            ))}
            {/* Only show loading rows for the initial expansion */}
            {revision.nextPage.isLoading &&
              revision.issues.length === 0 &&
              _.times(loadingRowCount, () => IssueItemLoading)}
          </div>
          {/* Only show the "Load more" button while issues remain AFTER the initial expansion w/o errors */}
          {remainingIssues > 0 && revision.issues.length !== 0 && !revision.nextPage.errors && (
            <LoadIssuesButton
              onClick={() => {
                dispatch(fetchIssuesForRevision(revision.revisionId));
              }}
              isLoading={revision.nextPage.isLoading}
            >
              Load more
            </LoadIssuesButton>
          )}
          {revision.nextPage.errors && (
            <>
              <p className={css.error}>Sorry. We ran into a problem. Please try again.</p>
              <LoadIssuesButton
                onClick={() => {
                  dispatch(fetchIssuesForRevision(revision.revisionId));
                }}
                isLoading={revision.nextPage.isLoading}
              >
                Try again
              </LoadIssuesButton>
            </>
          )}
        </div>
      )}
    </List.RowContainer>
  );
};

export default RevisionItem;
